import { getV2, postV2, patchV2, deletedV2 } from 'api/request'

// v2 Calendar Event collection — the generic /core/calendar/event endpoint the Leads and
// Service modules already migrated their own event wrappers onto. This module talks to it
// directly: no lead/issue scoping on the base list, no `scheme` param (the endpoint does
// not support one).
//
// List endpoint query params (per v2/schema/core, re-checked 2026-09-17): start, end,
// target_objects, target_groups, calendar, user, owner, assigned_to, participant_user,
// status, `q` (free-text — the endpoint uses `q`, NOT `search`), offset, size.
// `assigned_to` = comma-joined uids (same shape LeadList sends). `user` is a newer,
// broader "involves this user" filter (added server-side after `assigned_to` existed) —
// CalendarPage's list FilterBar uses `user` for its "User" field, not `assigned_to`.
// No `event_type` filter.
//
// v2 registers the bare collection path WITH a trailing slash; the {uid} and sub-paths
// WITHOUT one.
export const API_EVENT = '/core/calendar/event'

// filters: { status?, search?, user?, assigned_to?, calendar?, target_objects?,
// target_groups? } — the FE-internal `search` key maps to the wire param `q`.
// `calendar` / `user` / `assigned_to` are comma-joined id lists. target_objects is a
// comma-joined "type:uid" string (e.g. "lead/Lead:154,project/Issue:322"), only used
// when the calendar is opened scoped to a parent record. target_groups is a comma-joined
// "type:uid" string too, but scoped to a *group* record (e.g. "lead/Scheme:123" — a lead
// scheme, not a single lead), matching a `target_group_type`/`target_group_uid` pair from
// `getEventLinkGroups()` below.
export const getEventList = (start, end, filters = {}, offset = 0, size = 200) => (
  getV2(`${API_EVENT}/`, {
    urlParams: {
      start,
      end,
      status: filters.status || undefined,
      q: filters.search || undefined,
      user: filters.user || undefined,
      assigned_to: filters.assigned_to || undefined,
      calendar: filters.calendar || undefined,
      target_objects: filters.target_objects || undefined,
      target_groups: filters.target_groups || undefined,
      offset,
      size,
    },
  })
)

// Faceting endpoint — one row per distinct target *group* (a lead scheme, a project,
// etc.) currently linked to at least one event, with a count. Not paginated (plain
// array, no PaginatedResponse wrapper). Powers the "Lead/Project Type" filter's option
// list: each row's `target_group_type`/`target_group_uid` pair is the exact
// "type:uid" shape `target_groups` (above) expects. Takes the same filter params as the
// list endpoint (start/end/status/q/user/assigned_to/calendar/target_objects/
// target_groups) to scope the facet counts, but CalendarPage calls it bare (unfiltered)
// to build a static option list, same as `getCalendarList()`.
export const getEventLinkGroups = () => getV2(`${API_EVENT}/link-groups`)

export const getEvent = (eventKey) => getV2(`${API_EVENT}/${eventKey}`)

export const createEvent = (data) => postV2(`${API_EVENT}/`, data)

export const updateEvent = (eventKey, data) => patchV2(`${API_EVENT}/${eventKey}`, data)

export const deleteEvent = (eventKey) => deletedV2(`${API_EVENT}/${eventKey}`)

// Links are managed through dedicated sub-endpoints (not the event PATCH body):
//   POST   /core/calendar/event/{uid}/link   LinkIn
//   PATCH  /core/calendar/event/link/{pk}    LinkUpdateIn
//   DELETE /core/calendar/event/link/{pk}
// On CREATE, links[] can still be passed inline in the EventIn body.
export const addEventLink = (eventKey, link) => postV2(`${API_EVENT}/${eventKey}/link`, link)

export const updateEventLink = (linkPk, data) => patchV2(`${API_EVENT}/link/${linkPk}`, data)

export const deleteEventLink = (linkPk) => deletedV2(`${API_EVENT}/link/${linkPk}`)
